import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  coatingThicknessFormField,
  tminFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoCourseJSON {
  type: GeckoFeatureModelType.Course
  name: string
  feature_geometry: {
    y_end: number
  }
  stress_tolerance_override: number | undefined
  tmin: number | undefined
  nominal: {
    wall_thickness: number | undefined
    coating_thickness: number | undefined
  }
}

export const NewGeckoCourseJSON: GeckoCourseJSON = {
  type: GeckoFeatureModelType.Course,
  name: 'Course',
  feature_geometry: {
    y_end: 0,
  },
  tmin: undefined,
  stress_tolerance_override: undefined,
  nominal: {
    wall_thickness: undefined,
    coating_thickness: undefined,
  },
}

export class GeckoShellCourse extends GeckoEditableFeature {
  public readonly name: string
  public readonly type = GeckoFeatureModelType.Course
  public readonly geometry: GeckoComponentRect
  public readonly wallNominalThickness: GeckoMeasurement | undefined
  public readonly coatingNominalThickness: GeckoMeasurement | undefined
  public readonly stressTolerance: GeckoMeasurement | undefined
  public readonly tmin: GeckoMeasurement | undefined

  private constructor(
    id: string,
    name: string,
    geometry: GeckoComponentRect,
    wallNominal: GeckoMeasurement | undefined,
    coatingNominal: GeckoMeasurement | undefined,
    stressTolerance: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined
  ) {
    super(id)
    this.name = name
    this.geometry = geometry
    this.wallNominalThickness = wallNominal
    this.coatingNominalThickness = coatingNominal
    this.stressTolerance = stressTolerance
    this.tmin = tmin
  }

  public get componentID() {
    return this.geometry.componentID
  }

  public static CreateArrayFromJSON(
    json: unknown,
    componentGeometry: GeckoComponentRect,
    thicknessMeasurementUnit: GeckoMeasurementUnit,
    jsonIDs?: Array<string>
  ): ReadonlyArray<GeckoShellCourse> | undefined {
    const arr = json as
      | Array<Record<string, string | number | Record<string, unknown>>>
      | undefined
    if (!arr) return undefined

    try {
      const basicCourseData: {
        id: string
        name: string
        yEnd: number
        nominal: GeckoCourseJSON['nominal']
        stressTolerance?: number
        tmin?: number
      }[] = arr
        .map((course, index) => {
          const featureGeometry = course.feature_geometry as Record<
            string,
            number
          >
          const yEnd =
            parseNumber(featureGeometry?.y_end ?? course.y_end) ??
            componentGeometry.end.y.num
          const nominal = course.nominal as GeckoCourseJSON['nominal']
          const stressTolerance = course.stress_tolerance_override as
            | number
            | undefined
          const tmin = course.tmin as number | undefined

          return {
            id: jsonIDs ? jsonIDs[index] : String(index),
            name: parseString(course.name) ?? '',
            yEnd,
            nominal,
            stressTolerance,
            tmin,
          }
        })
        .toSorted((courseA, courseB) => {
          return courseA.yEnd - courseB.yEnd
        })

      let priorYEnd = componentGeometry.start.y.num

      return basicCourseData.map((bcdIterator) => {
        if (priorYEnd > bcdIterator.yEnd) {
          throw Error(
            `Shell courses not sorted for ${componentGeometry.componentID}`
          )
        }

        const shellCourseBounds = GeckoComponentRect.CreateFromXYs(
          componentGeometry.componentID,
          componentGeometry.start.x.num,
          priorYEnd,
          componentGeometry.end.x.num,
          bcdIterator.yEnd,
          componentGeometry.x.unit,
          componentGeometry.y.unit
        )
        const wallNominal = parseNumber(bcdIterator.nominal?.wall_thickness)
        const coatingNominal = parseNumber(
          bcdIterator.nominal?.coating_thickness
        )
        const stressTolerance = parseNumber(bcdIterator?.stressTolerance)
        const tmin = parseNumber(bcdIterator?.tmin)

        const result = new GeckoShellCourse(
          bcdIterator.id,
          bcdIterator.name,
          shellCourseBounds,
          wallNominal === undefined
            ? undefined
            : weightThicknessValue(
                GeckoMeasurement.CreateFromValue(
                  wallNominal,
                  thicknessMeasurementUnit
                )
              ),
          coatingNominal === undefined
            ? undefined
            : weightThicknessValue(
                GeckoMeasurement.CreateFromValue(
                  coatingNominal,
                  thicknessMeasurementUnit
                )
              ),
          stressTolerance === undefined
            ? undefined
            : GeckoMeasurement.CreateFromValue(
                stressTolerance,
                GeckoMeasurementUnit.Megapascals
              ),
          tmin === undefined
            ? undefined
            : GeckoMeasurement.CreateFromValue(tmin, thicknessMeasurementUnit)
        )
        priorYEnd = bcdIterator.yEnd
        return result
      })
    } catch (e) {
      console.error(e)
      return undefined
    }
  }

  public static CreateMapFromJSON(
    coursesJSON: Record<string, GeckoCourseJSON>,
    componentGeometry: GeckoComponentRect,
    thicknessMeasurementUnit: GeckoMeasurementUnit
  ): Map<string, GeckoShellCourse> {
    const array = this.CreateArrayFromJSON(
      Object.values(coursesJSON),
      componentGeometry,
      thicknessMeasurementUnit,
      Object.keys(coursesJSON)
    )
    const result = new Map<string, GeckoShellCourse>()
    array?.forEach((course) => {
      result.set(course.id, course)
    })
    return result
  }

  public static CreateFromJSON(
    componentID: string,
    courseID: string,
    courseJSON: GeckoCourseJSON
  ): GeckoShellCourse {
    // This is a fake component bounds, because all we really care about is y_end
    const pseudoComponentBounds = GeckoComponentRect.CreateFromXYs(
      componentID,
      Number.NEGATIVE_INFINITY,
      0,
      Number.POSITIVE_INFINITY,
      courseJSON.feature_geometry.y_end as number,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    // This is just so we can reuse the creation workflow and don't have to re-write it
    const instantiatedMap = GeckoShellCourse.CreateMapFromJSON(
      { [courseID]: courseJSON } as Record<string, GeckoCourseJSON>,
      pseudoComponentBounds,
      GeckoMeasurementUnit.Meters
    )

    const instantiatedCourse = instantiatedMap.get(courseID)
    if (!instantiatedCourse) {
      throw new Error('Unable to retrieve the instantiated Shell Course!')
    }

    return instantiatedCourse
  }

  public asJSON() {
    return generateShellCourse(
      this.name,
      this.geometry.y.max,
      this.wallNominalThickness,
      this.coatingNominalThickness,
      this.stressTolerance,
      this.tmin
    )
  }
}

export function generateShellCourse(
  name: string,
  yEnd: GeckoMeasurement,
  wallNominalThickness: GeckoMeasurement | undefined,
  coatingNominalThickness: GeckoMeasurement | undefined,
  stressTolerance: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined
): GeckoCourseJSON {
  const courseJSON: GeckoCourseJSON = {
    type: GeckoFeatureModelType.Course,
    name: name,
    feature_geometry: {
      y_end: yEnd.toNumber(GeckoMeasurementUnit.Meters),
    },
    stress_tolerance_override: undefined,
    nominal: {
      wall_thickness: undefined,
      coating_thickness: undefined,
    },
    tmin: undefined,
  }

  if (wallNominalThickness !== undefined) {
    courseJSON.nominal.wall_thickness = wallNominalThickness.toNumber(
      GeckoMeasurementUnit.Meters
    )
  }

  if (stressTolerance !== undefined) {
    courseJSON.stress_tolerance_override = stressTolerance.toNumber(
      GeckoMeasurementUnit.Megapascals
    )
  }

  if (coatingNominalThickness !== undefined) {
    courseJSON.nominal.coating_thickness = coatingNominalThickness.toNumber(
      GeckoMeasurementUnit.Meters
    )
  }

  if (tmin !== undefined) {
    courseJSON.tmin = tmin.toNumber(GeckoMeasurementUnit.Meters)
  }

  return courseJSON
}

export const ShellCourseFormFields = (courseJSON: GeckoCourseJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Course Name',
      value: courseJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: courseJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    wallThicknessFormField(courseJSON.nominal.wall_thickness),
    coatingThicknessFormField(courseJSON.nominal.coating_thickness),
    {
      dataKey: 'stress_tolerance_override',
      label: 'Stress Tolerance',
      value: courseJSON.stress_tolerance_override,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    tminFormField(courseJSON.tmin),
  ]
}

export const ShellCourseJSONFromFormFields = (formFields: FormField[]) => {
  const wallThickness = getFormFieldByDataKey(formFields, 'wall_thickness')
  const coatingThickness = getFormFieldByDataKey(
    formFields,
    'coating_thickness'
  )
  const shellTolerance = getFormFieldByDataKey(
    formFields,
    'stress_tolerance_override'
  )
  const tmin = getFormFieldByDataKey(formFields, 'tmin')
  return generateShellCourse(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    wallThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          wallThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    coatingThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          coatingThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    shellTolerance?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          shellTolerance?.value as number,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    tmin?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          tmin?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined
  )
}

export const ShellCourseFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.Course,
  title: 'Courses',
  singularName: 'Course',
  fields: ['name', 'y_end', 'wall_thickness', 'tmin'],
  sortBy: [{ fieldName: 'y_end', order: 'asc' }],
}
